/**
 * @file: VoiceRecordingBar.tsx
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * Inline recording indicator shown in place of a text input while a voice
 * message / dictation is being recorded (mobile only). Renders a
 * left-scrolling waveform driven by live mic metering plus an elapsed timer.
 * Cancel / complete live as the host's flanking round buttons.
 *
 * Shared: the chat composer and the AI assistant's input bar both mount it.
 */

import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FC, memo, useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import { BehaviorSubject } from 'rxjs';

/** Number of waveform bars; the array shifts left on each metering tick. */
const BAR_COUNT = 32;

const formatElapsed = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export const VoiceRecordingBar: FC<{
  /** Normalized 0..1 mic level stream (latest value pushed per tick). */
  meter$: BehaviorSubject<number>;
}> = memo(({ meter$ }) => {
  const { rpx } = useResponsiveContext();
  const [levels, setLevels] = useState<number[]>(() =>
    new Array(BAR_COUNT).fill(0),
  );
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(Date.now());

  useEffect(() => {
    startRef.current = Date.now();
    setElapsed(0);
    setLevels(new Array(BAR_COUNT).fill(0));
    const timer = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 250);
    // Subscribe directly (no distinctUntilChanged) — repeated equal levels
    // still need to scroll the waveform along.
    const sub = meter$.subscribe(level => {
      setLevels(prev => {
        const next = prev.slice(1);
        next.push(level);
        return next;
      });
    });
    return () => {
      clearInterval(timer);
      sub.unsubscribe();
    };
  }, [meter$]);

  const maxBar = rpx(24);
  const minBar = rpx(3);

  return (
    <View
      style={{
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F1F5F9',
        borderRadius: rpx(22),
        borderWidth: 1,
        borderColor: '#E2E8F0',
        height: rpx(40),
        paddingHorizontal: rpx(16),
        gap: rpx(10),
      }}
    >
      <View
        style={{
          flex: 1,
          flexDirection: 'row',
          alignItems: 'center',
          height: '100%',
          gap: rpx(2),
          overflow: 'hidden',
        }}
      >
        {levels.map((level, i) => (
          <View
            key={i}
            style={{
              flex: 1,
              height: Math.max(minBar, level * maxBar),
              borderRadius: rpx(2),
              backgroundColor: '#3b82f6',
            }}
          />
        ))}
      </View>
      <Typography.Label
        color="#64748B"
        size={rpx(13)}
        style={{
          fontVariant: ['tabular-nums'],
          minWidth: rpx(36),
          textAlign: 'right',
        }}
      >
        {formatElapsed(elapsed)}
      </Typography.Label>
    </View>
  );
});
